import { useCallback, useEffect, useMemo, useState } from "react";
import { API, getCached, setCached } from "../../../cache";
import { ComboBox } from "../../common/components";
import { canViewTaskByVisibility } from "../../tasks/pages/TasksViewV2Page";
import { canViewDefectByVisibility } from "../../defects/pages/DefectsViewV2Page";
import ProjectTimeline from "./ProjectTimeline";
import "../styles/ProjectPulseTab.css";

const ALL_TASK_STATUSES = "Open,In Progress,Completed,Cancelled,On Hold,NA";
// Forward-looking window: today + the next 6 days (7 days total). Previous/Next
// still page a full week at a time for occasional look-back, via weekOffset.
const WINDOW_DAYS_BACK = 0;
const WINDOW_DAYS_FORWARD = 6;

function flattenProjectOptions(customersProjects) {
  const rows = [];
  (customersProjects || []).forEach((customer) => {
    (customer.projects || []).forEach((project) => {
      if (!project.project_id) return;
      rows.push({
        value: project.project_id,
        label: `${project.project_name}${customer.customer_name ? ` (${customer.customer_name})` : ""}`,
        project_name: project.project_name,
      });
    });
  });
  return rows;
}

export default function ProjectPulseTab({ user, showToast }) {
  const [pulseMode, setPulseMode] = useState("project"); // "project" | "team"
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [teamOptions, setTeamOptions] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [tasks, setTasks] = useState([]);
  const [defects, setDefects] = useState([]);
  // roster.byUsername: Map<user_name, { teamName, displayName }>
  // roster.usernameByUserId: Map<user_id, user_name> — resolves a defect's `assigned_to` uuid
  // to the same identity key tasks use (`tmm_member` is a username), so a person with both
  // a task and a defect on the project merges into one row.
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [assignedOnly, setAssignedOnly] = useState(true);

  const selectedProject = projectOptions.find((p) => p.value === selectedProjectId) || null;
  const selectedTeam = teamOptions.find((t) => t.value === selectedTeamId) || null;
  const selectedEntity = pulseMode === "team" ? selectedTeam : selectedProject;

  // Project list — reuse the same cached active-projects fetch already used elsewhere on the dashboard.
  useEffect(() => {
    const cached = getCached("customers-projects-active");
    if (cached) {
      setProjectOptions(flattenProjectOptions(cached));
      return;
    }
    fetch(`${API}/customers-projects?active_only=true`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setCached("customers-projects-active", data);
        setProjectOptions(flattenProjectOptions(data));
      })
      .catch(() => showToast("Cannot load projects", "warn"));
  }, [showToast]);

  // Team roster — one-time fetch, used only to resolve a defect assignee's team
  // (tasks already carry team_name/member inline from GET /tasks).
  useEffect(() => {
    let cancelled = false;
    async function loadRoster() {
      try {
        const teams = await fetch(`${API}/teams`, { credentials: "include" }).then((r) => r.json());
        if (!cancelled) {
          setTeamOptions(
            (teams || [])
              .map((t) => ({ value: t.id, label: t.team_name, team_name: t.team_name }))
              .sort((a, b) => a.label.localeCompare(b.label))
          );
        }
        const memberLists = await Promise.all(
          (teams || []).map((t) =>
            fetch(`${API}/teams/${t.id}/members`, { credentials: "include" })
              .then((r) => r.json())
              .then((members) => ({ team_name: t.team_name, members }))
              .catch(() => ({ team_name: t.team_name, members: [] }))
          )
        );
        if (cancelled) return;
        const byUsername = new Map();
        const usernameByUserId = new Map();
        memberLists.forEach(({ team_name, members }) => {
          (members || []).forEach((m) => {
            if (!m.user_name) return;
            byUsername.set(m.user_name, { teamName: team_name, displayName: m.display_name });
            if (m.id) usernameByUserId.set(m.id, m.user_name);
          });
        });
        setRoster({ byUsername, usernameByUserId });
      } catch {
        if (!cancelled) setRoster({ byUsername: new Map(), usernameByUserId: new Map() });
      }
    }
    loadRoster();
    return () => { cancelled = true; };
  }, []);

  const fetchProjectData = useCallback(async (projectId, projectName) => {
    setLoading(true);
    try {
      const taskParams = new URLSearchParams({
        view: "all",
        project_id_in: projectId,
        status_in: ALL_TASK_STATUSES,
        page_size: "2000",
      });
      const defectParams = new URLSearchParams({
        view: "all",
        project_name_in: projectName,
        page_size: "2000",
      });
      const [taskResp, defectResp] = await Promise.all([
        fetch(`${API}/tasks?${taskParams.toString()}`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/defects?${defectParams.toString()}`, { credentials: "include" }).then((r) => r.json()),
      ]);
      const taskItems = Array.isArray(taskResp) ? taskResp : (taskResp.items || []);
      const defectItems = Array.isArray(defectResp) ? defectResp : (defectResp.items || []);
      setTasks(taskItems.filter((t) => canViewTaskByVisibility(t, user)));
      setDefects(defectItems.filter((d) => canViewDefectByVisibility(d, user)));
    } catch {
      showToast("Cannot load project tasks/defects", "warn");
      setTasks([]);
      setDefects([]);
    }
    setLoading(false);
  }, [user, showToast]);

  const fetchTeamData = useCallback(async (teamId, teamName) => {
    setLoading(true);
    try {
      const taskParams = new URLSearchParams({
        view: "all",
        team_name_in: teamName,
        status_in: ALL_TASK_STATUSES,
        page_size: "2000",
      });
      const defectParams = new URLSearchParams({
        view: "all",
        owning_team_ids: teamId,
        page_size: "2000",
      });
      const [taskResp, defectResp] = await Promise.all([
        fetch(`${API}/tasks?${taskParams.toString()}`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/defects?${defectParams.toString()}`, { credentials: "include" }).then((r) => r.json()),
      ]);
      const taskItems = Array.isArray(taskResp) ? taskResp : (taskResp.items || []);
      const defectItems = Array.isArray(defectResp) ? defectResp : (defectResp.items || []);
      setTasks(taskItems.filter((t) => canViewTaskByVisibility(t, user)));
      setDefects(defectItems.filter((d) => canViewDefectByVisibility(d, user)));
    } catch {
      showToast("Cannot load team tasks/defects", "warn");
      setTasks([]);
      setDefects([]);
    }
    setLoading(false);
  }, [user, showToast]);

  useEffect(() => {
    if (pulseMode === "project") {
      if (!selectedProject) { setTasks([]); setDefects([]); return; }
      fetchProjectData(selectedProject.value, selectedProject.project_name);
      return;
    }
    if (!selectedTeam) { setTasks([]); setDefects([]); return; }
    fetchTeamData(selectedTeam.value, selectedTeam.team_name);
  }, [pulseMode, selectedProject, selectedTeam, fetchProjectData, fetchTeamData]);

  // Group tasks/defects into Team -> Person rows. In "team" mode everything
  // fetched already belongs to the one selected team, so it collapses to a
  // single group (still bucketed by person, same merge-by-identity logic).
  const groups = useMemo(() => {
    if (!roster) return null;
    const teams = new Map(); // teamName -> Map<userKey, { displayName, tasks: [], defects: [] }>

    const getBucket = (teamName, userKey, displayName) => {
      const tName = teamName || "Unassigned";
      if (!teams.has(tName)) teams.set(tName, new Map());
      const people = teams.get(tName);
      if (!people.has(userKey)) people.set(userKey, { displayName: displayName || userKey, tasks: [], defects: [] });
      return people.get(userKey);
    };

    const fixedTeamName = pulseMode === "team" ? (selectedTeam?.team_name || "Unassigned") : null;

    tasks.forEach((t) => {
      const userKey = t.tmm_member || "unassigned";
      const displayName = t.tmm_member || "Unassigned";
      const bucket = getBucket(fixedTeamName ?? t.tmm_team_name, userKey, displayName);
      bucket.tasks.push(t);
    });

    defects.forEach((d) => {
      if (!d.assigned_to) {
        const bucket = getBucket(fixedTeamName ?? null, "unassigned", "Unassigned");
        bucket.defects.push(d);
        return;
      }
      const userName = roster.usernameByUserId.get(d.assigned_to);
      const resolved = userName ? roster.byUsername.get(userName) : null;
      const userKey = userName || d.assigned_to;
      const bucket = getBucket(fixedTeamName ?? resolved?.teamName, userKey, resolved?.displayName || d.assigned_to_name);
      bucket.defects.push(d);
    });

    return Array.from(teams.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([teamName, people]) => ({
        teamName,
        people: Array.from(people.values()).sort((a, b) => a.displayName.localeCompare(b.displayName)),
      }));
  }, [tasks, defects, roster, pulseMode, selectedTeam]);

  const hasData = groups && groups.some((g) => g.people.length > 0);

  return (
    <div className="ppt-root">
      <div className="ppt-toolbar">
        <div className="ppt-week-nav">
          <button
            className={`ppt-week-btn${pulseMode === "project" ? " ppt-week-btn--today" : ""}`}
            onClick={() => setPulseMode("project")}
          >
            By Project
          </button>
          <button
            className={`ppt-week-btn${pulseMode === "team" ? " ppt-week-btn--today" : ""}`}
            onClick={() => setPulseMode("team")}
          >
            By Team
          </button>
        </div>

        {pulseMode === "project" ? (
          <ComboBox
            options={projectOptions}
            value={selectedProjectId}
            onChange={setSelectedProjectId}
            placeholder="Select a project…"
            className="ppt-project-picker"
          />
        ) : (
          <ComboBox
            options={teamOptions}
            value={selectedTeamId}
            onChange={setSelectedTeamId}
            placeholder="Select a team…"
            className="ppt-project-picker"
          />
        )}

        {selectedEntity && (
          <div className="ppt-week-nav">
            <button className="ppt-week-btn" onClick={() => setWeekOffset((w) => w - 1)}>← Previous</button>
            <button className="ppt-week-btn ppt-week-btn--today" onClick={() => setWeekOffset(0)}>Today</button>
            <button className="ppt-week-btn" onClick={() => setWeekOffset((w) => w + 1)}>Next →</button>
          </div>
        )}
        {selectedEntity && (
          <div className="ppt-week-nav">
            <button
              className={`ppt-week-btn${!assignedOnly ? " ppt-week-btn--today" : ""}`}
              onClick={() => setAssignedOnly(false)}
            >
              All resources
            </button>
            <button
              className={`ppt-week-btn${assignedOnly ? " ppt-week-btn--today" : ""}`}
              onClick={() => setAssignedOnly(true)}
            >
              Assigned only
            </button>
          </div>
        )}
      </div>

      {!selectedEntity && (
        <div className="state-empty ppt-empty">
          {pulseMode === "project"
            ? "Select a project to see its task & defect timeline."
            : "Select a team to see its allocation."}
        </div>
      )}

      {selectedEntity && loading && (
        <div className="state-loading ppt-empty">Loading…</div>
      )}

      {selectedEntity && !loading && !hasData && (
        <div className="state-empty ppt-empty">
          {pulseMode === "project"
            ? "No tasks or defects visible to you on this project."
            : "No tasks or defects visible to you for this team."}
        </div>
      )}

      {selectedEntity && !loading && hasData && (
        <ProjectTimeline
          groups={groups}
          weekOffset={weekOffset}
          windowDaysBack={WINDOW_DAYS_BACK}
          windowDaysForward={WINDOW_DAYS_FORWARD}
          assignedOnly={assignedOnly}
        />
      )}
    </div>
  );
}
